import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription } from 'rxjs';

import { AppConfigService } from '../../../app-config.service';
import { DatePipe } from '../../pipes/date.pipe';
import { DesjardinsCurrencyPipe } from '../../pipes/dd-currency.pipe';
import { HourPipe } from '../../pipes/hour.pipe';
import { OpenModalService } from '../../services/open-modal/open-modal.service';
import { PreautorisationEnLigneState } from '../../../tool-preautorisation/shared/preautorisation-en-ligne.state';
import { PreautorisationFlowHelper } from '../../../tool-preautorisation/experimente/helpers/preautorisation-flow-helper/preautorisation-flow-helper';
import { PreautorisationModel } from 'src/app/tool-preautorisation/experimente/models/preautorisation.model';
import { Router } from '@angular/router';
import { StatusPreautorisation } from '../../../shared/modeles/variables';
import { Store } from '@ngxs/store';
import { TextualDatePipe } from '../../pipes/textual-date.pipe';
import { ToolPreautorisationService } from '../../../tool-preautorisation/tool-preautorisation.service';
import { get } from 'lodash';

@Component({
  selector: 'app-content-preautorisation-dashboard',
  templateUrl: './content-preautorisation-dashboard.component.html',
  styleUrls: ['./content-preautorisation-dashboard.component.scss'],
  standalone: false
})
export class ContentPreautorisationDashboardComponent implements OnInit, OnDestroy {
  isSecondCoemprunteur$: Observable<boolean> = this._toolPreautorisationService.isSecondCoemprunteur$;
  seeDetailsText = 'dashboard.active-preaut.btn-details';
  dashboardComponent: unknown = this._toolPreautorisationService.dashboardComponent;

  preautorisation$: Observable<PreautorisationModel> = this._toolPreautorisationService.getPreautorisation$;
  preautorisation: PreautorisationModel;

  subscriptions: Subscription = new Subscription();

  @Input()
  content: any;

  @Input()
  lienBtn: string;

  @Input()
  isDefaultBtnColor = true;

  constructor(
    private readonly _router: Router,
    private readonly _appConfigService: AppConfigService,
    private readonly _openModalService: OpenModalService,
    private readonly _store: Store,
    private readonly _toolPreautorisationService: ToolPreautorisationService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(this.preautorisation$.subscribe(data => (this.preautorisation = data)));
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  async navigateToPreaut() {
    await this._toolPreautorisationService.initPreautorisationEnLigne();
    this._router.navigateByUrl(this.lienBtn);
  }

  async navigateToInvitationCodePage() {
    await this._router.navigateByUrl('preautorisation-simplifiee/invitation');
  }

  openCancelModal() {
    this._openModalService.openModalCancelPreapproval();
  }

  get isPreautButtonVisible(): boolean {
    return !this.data || !this.preautorisation.statut;
  }

  get isDsdVersionVisible(): boolean {
    return !!this.dashboardComponent;
  }

  get preautButton(): { id: string; label: string } {
    return {
      id: 'btn-avantages-preautorisation',
      label: this.content.texteBtn
    };
  }

  get data() {
    if (this.preautorisation && this.preautorisation.statut) {
      return this[this.preautorisation.statut];
    }

    return undefined;
  }

  private get [StatusPreautorisation.Acceptee]() {
    return {
      id: 'statut-preautorisation-acceptee',
      isPreautAcceptee: true
    };
  }

  private get [StatusPreautorisation.ContreOffreAcceptee]() {
    return {
      id: 'statut-preautorisation-contre-offre-acceptee',
      isPreautAcceptee: true
    };
  }

  private get [StatusPreautorisation.EnAttente]() {
    return {
      id: 'statut-preautorisation-en-attente',
      translationKey: 'dashboard.active-preaut.en-attente',
      isStatusBoxVisible: true,
      isClickToCallVisible: true
    };
  }

  private get [StatusPreautorisation.EnAttenteTaux]() {
    return {
      ...this[StatusPreautorisation.EnAttente],
      id: 'statut-preautorisation-en-attente-taux'
    };
  }

  private get [StatusPreautorisation.EnAttenteCoemprunteur]() {
    const dateExpiration = new TextualDatePipe(this._appConfigService).transform(
      get(this.preautorisation, 'codeExpiration', '')
    );

    return {
      id: 'statut-preautorisation-en-attente-coemprunteur',
      translationKey: 'dashboard.active-preaut.en-attente-coemprunteur',
      translationParams: {
        dateExpiration,
        codeInvitation: get(this.preautorisation, 'codeInvitation', '')
      },
      isStatusBoxVisible: true,
      isCancelVisible: true,
      isClickToCallVisible: true,
      isSeeDetailsVisible: true
    };
  }

  private get [StatusPreautorisation.RefuseeSimplifie]() {
    return {
      id: 'statut-preautorisation-refusee-simplifie',
      translationKey:
        'dashboard.active-preaut.refusee-simplifie' +
        (PreautorisationFlowHelper.isCoemprunteur(this.preautorisation) ? '.coemprunteur' : '.unique'),
      extraClass: 'status-refusee-simplifie',
      isClickToCallVisible: true,
      isCancelVisible: true,
      isPhoneIconVisible: true
    };
  }

  private get [StatusPreautorisation.ContreOffreRefusee]() {
    return {
      id: 'statut-preautorisation-contre-offre-refusee',
      translationKey: 'dashboard.active-preaut.contre-offre-refusee',
      extraClass: 'status-refusee-simplifie',
      isClickToCallVisible: true,
      isCancelVisible: true,
      isPhoneIconVisible: true
    };
  }

  private get [StatusPreautorisation.ContreOffreEnAttente]() {
    const datePipe = new DatePipe(this._appConfigService);
    const hourPipe = new HourPipe(this._appConfigService);

    const prenom = this._appConfigService.configData.personne.prenom;
    const data = this._store.selectSnapshot(PreautorisationEnLigneState.donneesContreOffre);
    const isCoemprunteur = PreautorisationFlowHelper.isCoemprunteur(this.preautorisation);
    const isFirstCoemprunteur = PreautorisationFlowHelper.isFirstCoemprunteur(this.preautorisation);

    let translationKey = '';
    if (isCoemprunteur) {
      translationKey = isFirstCoemprunteur ? '.co1' : '.co2';
    } else {
      translationKey = '.unique';
    }

    this.seeDetailsText = 'dashboard.active-preaut.contre-offre-attente.consulter-demande';

    return {
      id: 'statut-preautorisation-contre-offre-attente',
      translationKey: 'dashboard.active-preaut.contre-offre-attente' + translationKey,
      translationParams: {
        prenom,
        heureExpirationContreOffre: hourPipe.transform(data.dateExpirationContreOffre),
        dateExpirationContreOffre: datePipe.transform(data.dateExpirationContreOffre),
        dateAnnulationContreOffre: datePipe.transform(data.dateContreOffreAnnulee),
        montant: new DesjardinsCurrencyPipe(this._appConfigService).transform(data.montantApprouve.toString())
      },
      isIntroVisible: !isFirstCoemprunteur,
      isStatusBoxVisible: true,
      isSeeDetailsVisible: true,
      isCancelVisible: !isCoemprunteur || isFirstCoemprunteur
    };
  }

  private get [StatusPreautorisation.EnAttenteSelectionTaux]() {
    this.seeDetailsText = 'dashboard.active-preaut.en-attente-selection-taux.choisir-taux';

    const isFirstCoemprunteur = this._store.selectSnapshot(PreautorisationEnLigneState.isFirstCoemprunteur);
    const dateExpirationSelectionTaux = this._store.selectSnapshot(
      PreautorisationEnLigneState.getDateExpirationSelectionTaux
    );

    return {
      id: 'statut-preautorisation-en-attente-selection-taux',
      translationKey:
        'dashboard.active-preaut.en-attente-selection-taux.' + (isFirstCoemprunteur ? 'co1' : 'unique-et-co2'),
      translationParams: {
        date: new DatePipe(this._appConfigService).transform(dateExpirationSelectionTaux),
        heure: new HourPipe(this._appConfigService).transform(dateExpirationSelectionTaux)
      },
      isIntroVisible: true,
      isStatusBoxVisible: true,
      isSeeDetailsVisible: !isFirstCoemprunteur,
      isCancelVisible: true
    };
  }
}
